import { Link, linkOptions } from '@tanstack/react-router';
import {
  CirclePauseIcon,
  PlayIcon,
  TriangleAlertIcon,
  UserRoundIcon,
} from 'lucide-react';

import { buttonVariants } from '@/components/ui/button';
import { StartAllChildren, TaskPrimaryAction } from '@/features/tasks';
import type { AttentionKind, TaskChoice } from '@/features/tasks';
import { StepKindIcon } from '@/shared/components/StepKindIcon';
import { formatDurationWords, formatExactTime } from '@/shared/utils/time';
import { HeldCountdown } from '@/shared/components/HeldCountdown';
import { useCanWork } from '@/features/workspaces/canWork';
import { attentionRowLink } from '../links';
import {
  ATTENTION_CHIP,
  ATTENTION_RAIL,
  ATTENTION_TINT,
  ROW,
  ROW_DETAIL,
  ROW_INTERACTIVE,
  ROW_META_CONTEXT,
  ROW_META_SPLIT,
  ROW_META_STATE,
  ROW_TITLE,
  STRETCHED_LINK,
  TINT_OVERLAY,
} from '../stateStyles';
import type { QueueRow as Row, QueueSeverity } from '../yourTurn';
import { Attempts } from './Attempts';
import { WhatItProduced } from './WhatItProduced';
import { RERUN_KINDS, choiceLabel } from '../rowChoices';
import { useHeldActions, useHeldFor } from '../heldActionsContext';

/**
 * One task in Your turn (spec 152 US2): why it is here and what to do, in
 * one row.
 *
 * - The state comes first, as an icon and words, then the step and how long
 *   the task has stood there, in words.
 * - The reason follows when one is known.
 * - The actions are what its state allows: a decision's own ways on; a run
 *   that went wrong is opened, or run again; a parent's waiting subtasks are
 *   started.
 *
 * Exactly one action in the queue wears the primary style, on the row the
 * person is on (`current`, FR-008).
 *
 * A reader (`useCanWork()` false) is offered only what reading allows: the run
 * or task opened, and What it produced (spec 193).
 */

/** What put the row here, in the reader's words. */
const KIND_LABELS: Record<AttentionKind, string> = {
  'failed-run': 'Run failed',
  'failed-workdir': 'Environment failed',
  orphaned: 'Off the board',
  'human-gate': 'Waiting for approval',
  branching: 'Waiting for a decision',
  stopped: 'Stopped',
  // Names the limit, not a figure: this row is visible to every member, and a
  // member may see neither the limit nor the spend (spec 072 FR-025).
  'spend-limit': 'Stopped by spend limit',
  'needs-account': 'Needs a personal account',
  'subtasks-to-start': 'Subtasks to start',
};

const SEVERITY_ICON: Record<QueueSeverity, typeof PlayIcon> = {
  wrong: TriangleAlertIcon,
  decision: UserRoundIcon,
  'to-start': PlayIcon,
};

/** Kinds whose story is told on a run, so "Open run" opens the Runs tab. */
const RUN_KINDS = new Set<AttentionKind>([
  'failed-run',
  'stopped',
  'spend-limit',
  'needs-account',
]);

export interface QueueRowProps {
  workspaceId: string;
  row: Row;
  /** The row the person is on: its first action is the primary one. */
  current: boolean;
  /** Within a group the parent is named above, so the row names only the step. */
  grouped: boolean;
  /**
   * Called once an action is held, to move focus on (spec 151 FR-005): the
   * person has decided, and the countdown needs no attention unless they
   * change their mind (spec 154).
   */
  onDone: () => void;
}

export function QueueRow({
  workspaceId,
  row,
  current,
  grouped,
  onDone,
}: QueueRowProps) {
  const link = attentionRowLink(workspaceId, row);
  // A spend limit is not a crash: nothing went wrong with the work, and the
  // row wears the waiting colour, so it pauses rather than warns.
  const Icon =
    row.kind === 'spend-limit' || row.kind === 'needs-account'
      ? CirclePauseIcon
      : SEVERITY_ICON[row.severity];
  const label =
    row.kind === 'subtasks-to-start' && row.toStart !== undefined
      ? `${String(row.toStart)} subtask${row.toStart === 1 ? '' : 's'} to start`
      : KIND_LABELS[row.kind];
  const waited = formatDurationWords(row.enteredAt);

  return (
    <li
      data-queue-task={row.taskId}
      // J and K move between rows (spec 154 US5), so a row takes focus.
      tabIndex={-1}
      className={`${link ? ROW_INTERACTIVE : ROW} focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset`}
    >
      <span
        aria-hidden="true"
        className={`${TINT_OVERLAY} ${ATTENTION_TINT[row.kind]}`}
      />
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-[3px] ${ATTENTION_RAIL[row.kind]}`}
      />

      <div className={`${ROW_TITLE} z-10`}>
        {link ? (
          <Link {...link} data-queue-title className={STRETCHED_LINK}>
            {row.title}
          </Link>
        ) : (
          // A draft has no board, so there is nowhere for this to go.
          <span
            data-queue-title
            className="text-foreground truncate text-sm font-medium"
          >
            {row.title}
          </span>
        )}
        <Attempts count={row.attempts} />
      </div>

      <p className={`${ROW_META_SPLIT} text-muted-foreground relative`}>
        {/* The state as an icon and words, never colour alone. */}
        <span
          className={`${ROW_META_STATE} inline-flex items-center gap-1 rounded-sm px-1 py-px text-2xs font-semibold ${ATTENTION_CHIP[row.kind]}`}
        >
          <Icon aria-hidden="true" className="size-3" strokeWidth={2} />
          {label}
        </span>
        <span className={ROW_META_CONTEXT}>
          {grouped ? null : `${row.workflowName} · `}
          <StepKindIcon kind={row.stepKind} />{' '}
          {row.stepLabel ?? 'No step on this board'}
          {waited ? (
            <span
              title={`On this step since ${formatExactTime(row.enteredAt)}`}
            >
              {` · ${waited} on this step`}
            </span>
          ) : null}
        </span>
      </p>

      <Refusal taskId={row.taskId} />

      {row.severity === 'decision' ? (
        <WhatItProduced workspaceId={workspaceId} row={row} />
      ) : null}

      {row.failureMessage ? (
        <p
          className={`${ROW_DETAIL} text-muted-foreground relative`}
          title={row.failureMessage}
        >
          {row.failureMessage}
        </p>
      ) : null}

      <Actions
        workspaceId={workspaceId}
        row={row}
        current={current}
        onDone={onDone}
      />
    </li>
  );
}

function Actions({
  workspaceId,
  row,
  current,
  onDone,
}: Omit<QueueRowProps, 'grouped'>) {
  const canWork = useCanWork();
  const { hold, undo } = useHeldActions();
  const { action } = useHeldFor(row.taskId);
  const lead = current ? 'primary' : 'quiet';
  // Above the stretched link, so the controls take their own clicks.
  const held =
    'relative z-10 col-span-2 flex flex-wrap items-center gap-1 pt-1.5';
  // On a phone the buttons share the row's width, 8px apart, instead of
  // sitting small at its start (spec 268). A menu's chevron keeps its size.
  const wrap = `${held} max-md:gap-2 max-md:[&>*]:flex-1`;
  const fill = 'max-md:gap-2 max-md:[&>button:not([aria-haspopup])]:flex-1';

  // Every action here waits six seconds before it is sent (spec 154 US2).
  const choose = (choice: TaskChoice) => {
    const held = hold({
      choice,
      rows: [row],
      label: choiceLabel(choice, row),
      subject: row.title,
    });
    if (held) onDone();
  };

  if (action) {
    return (
      <div className={held}>
        <HeldCountdown
          action={action}
          title={row.title}
          onUndo={() => {
            undo(action.id);
            focusRow(row.taskId);
          }}
        />
      </div>
    );
  }

  // A reader decides and starts nothing: the row opens where its story is
  // told, and nothing more (spec 193).
  if (!canWork) {
    const open = openLink(workspaceId, row);
    if (!open) return null;
    return (
      <div className={wrap}>
        <OpenLink open={open} title={row.title} current={current} />
      </div>
    );
  }

  if (row.severity === 'decision') {
    if (!row.hasBoard) return null;
    return (
      <div className={wrap}>
        <TaskPrimaryAction
          workspaceId={workspaceId}
          task={row.task}
          step={row.step}
          steps={row.steps}
          density="compact"
          emphasis={lead}
          onChoose={choose}
          className={fill}
        />
      </div>
    );
  }

  if (row.severity === 'to-start') {
    return (
      <div className={wrap}>
        <StartAllChildren
          workspaceId={workspaceId}
          task={row.task}
          count={row.toStart ?? 0}
          density="compact"
          emphasis={lead}
          onChoose={choose}
        />
      </div>
    );
  }

  const open = openLink(workspaceId, row);
  return (
    <div className={wrap}>
      {open ? (
        <OpenLink open={open} title={row.title} current={current} />
      ) : null}
      {RERUN_KINDS.has(row.kind) && row.hasBoard ? (
        <TaskPrimaryAction
          workspaceId={workspaceId}
          task={row.task}
          step={row.step}
          steps={row.steps}
          density="compact"
          emphasis="quiet"
          onChoose={choose}
          className={fill}
        />
      ) : null}
    </div>
  );
}

/** "Open run" or "Open task", the primary style on the current row. */
function OpenLink({
  open,
  title,
  current,
}: {
  open: NonNullable<ReturnType<typeof openLink>>;
  title: string;
  current: boolean;
}) {
  return (
    <Link
      {...open.options}
      aria-label={`${open.label}: ${title}`}
      className={buttonVariants({
        variant: current ? 'default' : 'outline',
        size: 'xs',
      })}
    >
      {open.label}
    </Link>
  );
}

/** Back to the row after Undo: its first control, once it is drawn again. */
function focusRow(taskId: string) {
  requestAnimationFrame(() => {
    document
      .querySelector<HTMLElement>(
        `li[data-queue-task="${CSS.escape(taskId)}"] button, li[data-queue-task="${CSS.escape(taskId)}"] a`,
      )
      ?.focus();
  });
}

/**
 * Why a sent action was refused, kept on the row (spec 154 FR-008) until the
 * row is acted on again. Not a live region: the command's toast already said
 * it once.
 */
function Refusal({ taskId }: { taskId: string }) {
  const { refusal } = useHeldFor(taskId);
  if (!refusal) return null;
  return (
    <p className={`${ROW_DETAIL} text-board-state-failed relative`}>
      Not sent: {refusal}
    </p>
  );
}

/**
 * Where a row that went wrong is looked into: the run for a failure, a stop
 * or a spend limit; the task itself for one off its board or whose
 * environment failed.
 */
function openLink(workspaceId: string, row: Row) {
  if (!row.hasBoard) return null;
  const onRuns = RUN_KINDS.has(row.kind);
  return {
    label: onRuns ? 'Open run' : 'Open task',
    options: linkOptions({
      to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
      params: { workspaceId, workflowKey: row.workflowKey },
      search: {
        task: row.taskId,
        tab: onRuns ? ('executions' as const) : ('details' as const),
      },
    }),
  };
}
